import React from 'react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { formatCurrency, formatCompactCurrency } from '../../applications/Traffic/utils'

export interface BudgetComparisonChartProps {
  scenarios: Array<{ name: string; totalBudget: number; expectedRevenue: number; expectedRoas: number; allocations: Record<string, number> }>
  selectedIndex: number
  channels: Array<{ channel: string; channelId: string; color: string }>
  currency: string
}

export function BudgetComparisonChart({ scenarios, selectedIndex, channels, currency }: BudgetComparisonChartProps) {
  const selectedScenario = scenarios[selectedIndex]
  if (!selectedScenario) return null

  const options: Highcharts.Options = {
    chart: { type: 'bar', height: 250 },
    title: { text: '' },
    xAxis: {
      categories: channels.map(c => c.channel),
      labels: { style: { fontSize: '11px' } }
    },
    yAxis: {
      title: { text: '' },
      labels: {
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
          return `$${((this.value as number) / 1000).toFixed(0)}k`
        }
      }
    },
    tooltip: {
      formatter: function (this: Highcharts.TooltipFormatterContextObject) {
        return `<b>${this.x}</b><br/>${this.series.name}: <b>${formatCurrency(this.y || 0, currency)}</b>`
      }
    },
    legend: { enabled: true },
    plotOptions: {
      bar: { borderRadius: 4 }
    },
    series: [
      {
        name: 'Current',
        type: 'bar',
        data: channels.map(c => scenarios[0]?.allocations[c.channelId]),
        color: '#94a3b8',
      },
      {
        name: selectedScenario.name,
        type: 'bar',
        data: channels.map(c => selectedScenario.allocations[c.channelId]),
        color: '#3b82f6',
      }
    ],
    credits: { enabled: false },
  }

  return (
    <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-6">
      <div className="lg:tw-col-span-2">
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
      <div className="tw-bg-slate-50 tw-rounded-lg tw-p-4">
        <h4 className="tw-font-semibold tw-text-slate-900 tw-mb-4">{selectedScenario.name} Scenario</h4>
        <div className="tw-space-y-4">
          <div>
            <p className="tw-text-sm tw-text-slate-500">Total Budget</p>
            <p className="tw-text-xl tw-font-bold tw-text-slate-900">
              {formatCompactCurrency(selectedScenario.totalBudget, currency)}
            </p>
          </div>
          <div>
            <p className="tw-text-sm tw-text-slate-500">Expected Revenue</p>
            <p className="tw-text-xl tw-font-bold tw-text-green-600">
              {formatCompactCurrency(selectedScenario.expectedRevenue, currency)}
            </p>
          </div>
          <div>
            <p className="tw-text-sm tw-text-slate-500">Expected ROAS</p>
            <p className="tw-text-xl tw-font-bold tw-text-blue-600">
              {selectedScenario.expectedRoas.toFixed(2)}x
            </p>
          </div>
          {selectedIndex > 0 && scenarios[0] != null && (
            <div className="tw-pt-2 tw-border-t tw-border-slate-200">
              <p className="tw-text-sm tw-text-green-600 tw-font-medium">
                +{formatCompactCurrency(selectedScenario.expectedRevenue - scenarios[0].expectedRevenue, currency)} vs Current
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
